import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { Auth } from 'aws-amplify';
import { Typography, withStyles } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import LoaderButton from './LoaderButton';
import ContentHeader from './ContentHeader';
import globalStyles from '../styles';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px 10px'
    },
    input: {
        margin: '20px 20px 10px'
    },
    button: {
        width: '200px',
        height: '56px',
        margin: '0 auto'
    },
    error: {
        margin: '0 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold',
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    disabledLink: {
        color: '#666',
        fontWeight: 'bold'
    }
});

class PasswordResetConfirmationForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: null,
            loading: false,
            confirmationCode: '',
            password: '',
            hasResentCode: false
        };
    }

    handleConfirmationSubmit = async (event, retryIfQuotaExceeded = true) => {
        event.preventDefault();
        const { email, handleAuth } = this.props;
        const { confirmationCode, password } = this.state;

        this.setState({ loading: true });

        try {
            await Auth.forgotPasswordSubmit(
                email.toLowerCase(),
                confirmationCode.trim(),
                password
            );
            await Auth.signIn(email.toLowerCase(), password);
            try {
                window.localStorage.removeItem('forgot_code_sent');
            } catch (localStorageErr) {
                console.error(localStorageErr);
            }
            handleAuth(true);
        } catch (e) {
            if (e.name === 'QuotaExceededError' && retryIfQuotaExceeded) {
                window.localStorage.clear();

                await this.handleConfirmationSubmit(event, false);
                return;
            }

            this.setState({
                error: e.message
            });
            this.setState({ loading: false });
        }
    };

    handleChange = event => {
        this.setState({
            [event.target.id]: event.target.value,
            error: false
        });
    };

    resendCode = async () => {
        const { email } = this.props;
        this.setState({
            hasResentCode: true
        });

        await Auth.forgotPassword(email.toLowerCase());

        try {
            window.localStorage.setItem(
                'forgot_code_sent',
                new Date().toISOString()
            );
        } catch (localStorageErr) {
            console.error(localStorageErr);
        }
    };

    validateConfirmationForm() {
        const { confirmationCode, password } = this.state;
        return confirmationCode.length > 0 && password.length > 0;
    }

    render() {
        const {
            confirmationCode,
            password,
            error,
            loading,
            hasResentCode
        } = this.state;
        const { classes, email } = this.props;

        return (
            <div className={classes.root}>
                <ContentHeader
                    text="Reset password"
                    id="confirm-email-header"
                />
                <form
                    className={classes.form}
                    onSubmit={this.handleConfirmationSubmit}
                >
                    <Typography variant="body1" style={{ fontSize: '1em' }}>
                        Please enter the latest verification code sent to{' '}
                        <strong>{email}</strong> and the new password you wish
                        to use. Please note the verification code will expire
                        after one hour.
                    </Typography>
                    <Typography variant="body1" style={{ marginTop: 10 }}>
                        {!hasResentCode && (
                            <span
                                onClick={this.resendCode}
                                className={classes.link}
                                aria-hidden="true"
                            >
                                Send another code
                            </span>
                        )}
                        {hasResentCode && (
                            <span className={classes.disabledLink}>
                                New code sent. Please check your inbox.
                            </span>
                        )}
                    </Typography>
                    <TextField
                        autoFocus
                        autoComplete="off"
                        className={classes.input}
                        id="confirmationCode"
                        label="Confirmation Code"
                        value={confirmationCode}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <TextField
                        autoComplete="off"
                        className={classes.input}
                        id="password"
                        type="password"
                        label="New password"
                        value={password}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <Typography className={classes.error}>{error}</Typography>
                    <LoaderButton
                        variant="outlined"
                        color="primary"
                        disabled={!this.validateConfirmationForm()}
                        className={classes.button}
                        type="submit"
                        isLoading={loading}
                        text="Reset password"
                        loadingText="Updating.."
                    />
                </form>
            </div>
        );
    }
}

PasswordResetConfirmationForm.propTypes = {
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(PasswordResetConfirmationForm);
